import { Component, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { CostComparison } from '../../models/cost-comparison/cost-comparison.model';

@Component({
    selector: 'fees-comparison-display',
    templateUrl: './fees-comparison-display.component.html',
    styleUrls: ['./fees-comparison-display.component.scss'],
    standalone: false
})
export class FeesComparisonDisplayComponent implements OnInit, OnChanges {

    private _costComparison!: CostComparison;
    @Input()
    set costComparison(display: CostComparison) {
        this._costComparison = display;
        this.fixFees();
    }

    get costComparison(): CostComparison {
        return this._costComparison;
    }

    processorSortDirection: 'asc' | 'desc' = 'desc';

    processorFees: Array<any> = [];
    additDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    assessDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    nonNegotDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    icDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

    feeDisplayColumns = ['label', 'amount', 'proposedAmount', 'savings'];
    procGroupColumns = ['item-description', 'current-provider', 'proposed-rates', 'spacer'];
    procDisplayColumns = ['label', 'volume', 'monthlyTransactions', 'transactionRate', 'transactionFee', 'totalFee', 'proposedRate', 'proposedFee', 'proposedTotalFee', 'savings'];
    flagText = 'Fee due to improper card acceptance';

    constructor() {
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        // console.log('changes:', changes);
        /* for (let propName in changes) {
         console.log('propName:', propName);
         let changedObj = changes[propName];
         if (!changedObj.isFirstChange()) {
          if (propName === 'saving') {
            console.log('changeObj:', changedObj);
            console.log('this.saving:', this.saving);
            if (this.saving) {
               this.saveFees();
             }
           }
         }
       }*/
    }

    fixFees() {
        this.processorFees = [];
        this.costComparison?.comparisonFeeGroupings?.processor?.fees?.forEach(f => {
            f.feeDetails?.forEach(fd => {
                const nfd = {
                    ...fd,
                    isFeeDetail: true,
                    parentFeeLabel: f.label
                };
                this.processorFees.push(nfd);
            });
        });
        this.sortProcessorFees();
    }

    /** Get display name from feeGroupingFriendlyNames only (never from originalFeeGroupings). Use default when missing or empty. */
    getFeeGroupingDisplayName(key: 'processor' | 'additional' | 'assessment' | 'nonNegotiable' | 'interchange', defaultName: string): string {
        const fn = this.costComparison?.feeGroupingFriendlyNames;
        if (fn && Object.prototype.hasOwnProperty.call(fn, key)) {
            const v = fn[key];
            return this.feeGroupingsFriendlyNameNotNull(v) ? (v ?? defaultName) : defaultName;
        }
        return defaultName;
    }

    getStatementFeesFriendlyName(friendlyName: string | undefined, defaultFeeName: string): string {
        return this.feeGroupingsFriendlyNameNotNull(friendlyName)
            ? (friendlyName ?? defaultFeeName)
            : defaultFeeName
    }

    feeGroupingsFriendlyNameNotNull(friendlyName: string | undefined): boolean {
        return friendlyName !== '' && friendlyName !== undefined && friendlyName !== null;
    }

    // CURRENT SUMMARY
    getCurrentTotalMonthlyFees() {
        const fg = this.costComparison.comparisonFeeGroupings;
        if (!fg) {
            return 0;
        }
        return fg.processor.totalAmount + fg.additional.totalAmount + fg.assessment.totalAmount + fg.nonNegotiable.totalAmount + fg.interchange.totalAmount;
    }

    getCurrentEffectiveRate() {
        const totalVolume = this.costComparison?.comparisonFeeGroupings?.processor?.totalVolume;
        if (!totalVolume) {
            return 0;
        }
        return this.getCurrentTotalMonthlyFees() / totalVolume;
    }

    // PROPOSED SUMMARY
    getProposedTotalMonthlyFees() {
        const fg = this.costComparison.comparisonFeeGroupings;
        if (!fg) {
            return 0;
        }
        return fg.processor.proposedTotalAmount + fg.additional.proposedTotalAmount + fg.assessment.proposedTotalAmount + fg.nonNegotiable.proposedTotalAmount + fg.interchange.proposedTotalAmount;
    }

    getProposedEffectiveRate() {
        const totalVolume = this.costComparison?.comparisonFeeGroupings?.processor?.totalVolume;
        if (!totalVolume) {
            return 0;
        }
        return this.getProposedTotalMonthlyFees() / totalVolume;
    }

    // SAVINGS SUMMARY
    getSavingsTotalMonthlyFees() {
        const fg = this.costComparison?.comparisonFeeGroupings;
        if (!fg) {
            return 0;
        }
        return this.getFeeGroupingSavings(fg.processor) + this.getFeeGroupingSavings(fg.additional) + this.getFeeGroupingSavings(fg.assessment) + this.getFeeGroupingSavings(fg.nonNegotiable) + this.getFeeGroupingSavings(fg.interchange);
    }

    /* HELPER FUNCTIONS */
    sum(...args: number[]) {
        return args.reduce((sum, current) => sum + current, 0);
    }

    getTotal(fees: any[], key: string) {
        return this.sum(...fees.filter(f => !!f[key]).map(f => f[key] || 0));
    }

    getFeeSavings(fee: any) {
        if (fee.proposedAmount) {
            return fee.amount - fee.proposedAmount;
        } else {
            return fee.amount;
        }
    }

    getProcFeeSavings(fee: any) {
        return fee.totalAmount - fee.proposedTotalAmount;
    }

    getFeeGroupingSavings(feeGrouping: any) {
        return feeGrouping.totalAmount - feeGrouping.proposedTotalAmount;
    }

    toggleProcessorTable() {
        if (this.processorFees.length === 0) {
            this.costComparison?.comparisonFeeGroupings?.processor?.fees?.forEach(f => {
                f?.feeDetails?.forEach(fd => {
                    const nfd = {
                        ...fd,
                        isFeeDetail: true,
                        parentFeeLabel: f.label
                    };
                    this.processorFees.push(nfd);
                });
            });
        }
        else this.processorFees = [];
    }

    toggleAdditionalTable() {
        if (this.additDataSource.data.length) {
            this.additDataSource = new MatTableDataSource();
        } else {
            this.additDataSource.data = this.costComparison?.comparisonFeeGroupings?.additional?.fees ?? [];
        }
    }

    toggleAssessmentTable() {
        if (this.assessDataSource.data.length) {
            this.assessDataSource = new MatTableDataSource();
        } else {
            this.assessDataSource.data = this.costComparison?.comparisonFeeGroupings?.assessment?.fees ?? [];
        }
    }

    toggleNonNegotiableTable() {
        if (this.nonNegotDataSource.data.length) {
            this.nonNegotDataSource = new MatTableDataSource();
        } else {
            this.nonNegotDataSource.data = this.costComparison?.comparisonFeeGroupings?.nonNegotiable?.fees ?? [];
        }
    }

    toggleInterchangeTable() {
        if (this.icDataSource.data.length) {
            this.icDataSource = new MatTableDataSource();
        } else {
            this.icDataSource.data = this.costComparison?.comparisonFeeGroupings?.interchange?.fees ?? [];
        }
    }

    processorFeeIsEmptyOrZero(processorFeeTransactionOrRateFee: any) {
        return processorFeeTransactionOrRateFee === null || processorFeeTransactionOrRateFee === undefined ||
            processorFeeTransactionOrRateFee === 0 || processorFeeTransactionOrRateFee === '0.00' ||
            processorFeeTransactionOrRateFee === 0.00 || processorFeeTransactionOrRateFee === '0';
    }

    sortProcessorFees() {
        this.processorSortDirection = this.processorSortDirection === 'asc' ? 'desc' : 'asc';
        this.processorFees = [...this.processorFees].sort((a, b) => {
            const valA = a.description ?? '';
            const valB = b.description ?? '';
            const cmp = valA.localeCompare(valB);
            return this.processorSortDirection === 'asc' ? cmp : -cmp;
        });
    }
}
